import { useMutation } from '@apollo/client';
import { GraphQLWhatsAppHeader } from 'modules/graphql';
import { submitWhatsAppTemplateMutation } from 'api/mutations/whatsAppSubmit';
import { useLocaleSettingsContext } from 'modules/campaigns/lib/contexts/LocaleSettingsContext';
import { useLocalesQueryContext } from 'modules/campaigns/lib/contexts/LocalesQueryContext';
import { ImageHeader, TextHeader, UrlButton, WhatsAppContentDefinition } from 'modules/graphql';
import { addNegativeToast, addPositiveToast } from 'modules/toastContainer';
import { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectIsWhatsAppNewCharLimitsFeatureFlagOn } from 'core/userContext';
import styled from 'styled-components';
import { NeutralBadge } from 'ui/badge';
import { ErrorBanner, WarningBanner } from 'ui/banner';
import { PrimaryButton, SecondaryButton } from 'ui/button';
import { ArrowBack, Edit16 } from 'ui/icons';
import { borderRadius, borderWidth1, colors, fontWeights, spacing, subtitle2 } from 'ui/theme';
import { Body1, H4, Subtitle1, zIndex } from 'ui/theme/typography';
import { Takeover } from 'ui/workflows/takeover';
import { displayContent, SettingsHeader, SettingsWrapper } from '../common';
import { useEditableFieldsContext } from '../context/EditableFieldsContext';
import { useTemplateQueryContext } from '../context/TemplateQueryContext';
import { useWhatsAppPreviewContext } from './context/WhatsAppPreviewContext';
import { useWhatsAppSenderContext } from './context/WhatsAppSenderContext';
import Preview from './preview/Preview';
import { SettingsForm } from './SettingsForm';
import { formatButtonValues, formatHeaderVal } from './utils';
import { DataPane } from '../common/PreviewWithDataDataPane';
import { MAX_MESSAGE_LENGTH_BUSINESS, NEW_MAX_MESSAGE_LENGTH_BUSINESS } from './constants';

export const BodyWrapper = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100%;
  position: relative;
`;

export const StyledErrorBanner = styled(ErrorBanner)`
  margin: ${spacing.plusHalf} ${spacing.plusHalf} 0 ${spacing.plusHalf};
`;

export const StyledWarningBanner = styled(WarningBanner)`
  margin: ${spacing.plusHalf} ${spacing.plusHalf} 0 ${spacing.plusHalf};
`;

export const BodyHeader = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${spacing.half};
  padding: ${spacing.plusHalf};
  border-bottom: ${borderWidth1} ${colors.borderPrimary};
`;

/**
 * @note The media breakpoint needed for the takeover is 1020px.
 * The takeover is set to 60% of the viewport, so the viewport width
 * would need to be 1700px in order for the takeover to be 1020px.
 *
 * @reference https://iterable.atlassian.net/browse/MOB-11279
 */
const TAKEOVER_BREAKPOINT = 1020 / 0.6;

export const BodyContent = styled.div`
  display: flex;
  gap: ${spacing.double};
  padding: ${spacing.plusHalf};

  @media (width <= calc(${TAKEOVER_BREAKPOINT}px)) {
    flex-direction: column;
  }
`;

const StyledTakeover = styled(Takeover)`
  z-index: ${zIndex.takeOver};
`;

const StyledSettingsWrapper = styled(SettingsWrapper)`
  height: fit-content;
  border: ${borderWidth1} ${colors.borderPrimary};
  border-radius: ${borderRadius.lg};
  &:not(:first-child){
    margin-top:${spacing.plusHalf};
  }
  &&.hasError {
    border-color: ${colors.borderDestructive}
  }
`;

export const FooterWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: ${spacing.half};
  padding: ${spacing.half} ${spacing.base};
  width: 100%;
`;

export const StyledH4 = styled(H4)`
  color: ${colors.textOnColor};
`;

export const StyledBody1 = styled(Body1)`
  margin-bottom: ${spacing.threeQuarter};
  font-weight: ${fontWeights.bold};
`;

export const StyledSubtitle = styled(Subtitle1)`
  margin-bottom: 0;
`;

const Disclaimer = styled.div`
  ${subtitle2};
  color:${colors.textSecondary};
  padding: ${spacing.half} 0 ${spacing.base} 0;
`;

const MISSING_USER_DATA = 'Invalid request: missing user data';

export const ReviewTemplateTakeover = ({ onClose }: { onClose: () => void }) => {
  const { liveTemplate } = useEditableFieldsContext();
  const { senders } = useWhatsAppSenderContext();
  const { whatsAppMessage, onRefreshPreview } = useWhatsAppPreviewContext();
  const { refetch } = useTemplateQueryContext();
  const {
    activeWhatsAppTemplateLocales,
    allProjectLocales,
    refetch: refetchLocales,
  } = useLocalesQueryContext();
  const { refetch: refetchLocaleSettings } = useLocaleSettingsContext();
  const isNewCharLimit = useSelector(selectIsWhatsAppNewCharLimitsFeatureFlagOn);
  const maxBusinessCharLimit = isNewCharLimit
    ? NEW_MAX_MESSAGE_LENGTH_BUSINESS
    : MAX_MESSAGE_LENGTH_BUSINESS;

  const { name, mediumData } = liveTemplate;

  const { buttons, header, locale, footer, whatsAppSenderId, whatsAppContentId, message } =
    mediumData as WhatsAppContentDefinition;

  const localeName = locale ? locale.name : allProjectLocales[0]?.tag;

  const dispatch = useDispatch();

  const getLocaleFriendlyName = (localeTag: string) => {
    const friendlyName = activeWhatsAppTemplateLocales?.find(
      ({ locale }) => locale.tag === localeTag,
    )?.locale.languageName;
    return friendlyName ?? localeTag;
  };

  const [submitWATemplate, submitData] = useMutation(submitWhatsAppTemplateMutation, {
    onCompleted({ submitTemplate }) {
      if (submitTemplate?.message) {
        dispatch(addNegativeToast(`Unable to submit ${name} for review. Please try again.`));
      } else {
        dispatch(addPositiveToast(`Success! ${name} submitted for review`));
        onClose();
        refetch();
        refetchLocales();
        refetchLocaleSettings();
      }
    },
    onError() {
      dispatch(addNegativeToast(`Unable to submit ${name} for review. Please try again.`));
    },
  });
  const getHandlebars = (str: string) => {
    return str.match(/\{\{([^{}]*)}}/g);
  };

  const dataFeedCount = liveTemplate?.dataFeedIds?.length;

  const templateHasHandlebars = useMemo(() => {
    if (getHandlebars((header as ImageHeader)?.mediaUrl || (header as TextHeader)?.text || '')) {
      return true;
    }
    if (getHandlebars(message || '')) {
      return true;
    }
    for (let i = 0; i < (buttons || [])?.length; i += 1) {
      if (getHandlebars((buttons?.[i] as UrlButton)?.url || '')) {
        return true;
      }
    }
    return false;
  }, [buttons, header, message]);

  const submitTemp = () => {
    const formattedButtons = buttons && buttons?.length > 0 ? formatButtonValues(buttons) : null;
    const senderPhoneNumber = senders?.find(
      (sender) => sender.id === whatsAppSenderId,
    )?.phoneNumber;

    submitWATemplate({
      variables: {
        name,
        language: localeName as string,
        // @ts-ignore
        category: mediumData?.whatsAppCategory,
        template: {
          header: formatHeaderVal(header as GraphQLWhatsAppHeader),
          footer,
          buttons: formattedButtons,
          body: {
            text: message,
          },
        },
        senderId: senderPhoneNumber,
        whatsAppContentId: whatsAppContentId as number,
      },
    });
  };

  return (
    <StyledTakeover
      size="60%"
      isOverlay
      noPadding
      onClose={onClose}
      HeaderBarContent={<StyledH4>Review</StyledH4>}
      Body={
        <BodyWrapper>
          <BodyHeader>
            <StyledSubtitle>{name}</StyledSubtitle>
            {localeName && <NeutralBadge>{getLocaleFriendlyName(localeName)}</NeutralBadge>}
          </BodyHeader>
          {submitData?.data?.submitTemplate?.message === MISSING_USER_DATA && (
            <StyledErrorBanner>
              At least one sample for your dynamic content is missing for this user. Add sample
              manually below.
            </StyledErrorBanner>
          )}
          {(whatsAppMessage || '').length > maxBusinessCharLimit && (
            <StyledWarningBanner
              BannerActions={
                <SecondaryButton onClick={onClose}>
                  <Edit16 /> Edit message
                </SecondaryButton>
              }
            >
              {isNewCharLimit ? (
                <>
                  The message <b>body</b> with sample content exceeds Meta’s 550-character limit.
                  This template may be rejected. Please shorten the message to comply with
                  submission guidelines before submitting.
                </>
              ) : (
                <>
                  Sample content in <b>body</b> exceeds the 1024-character limit. This template
                  could be rejected by Meta. Shorten your message to ensure compliance with
                  submission guidelines before submitting your template.
                </>
              )}
            </StyledWarningBanner>
          )}
          <BodyContent>
            <div style={{ flex: 1 }}>
              <StyledSettingsWrapper>
                <SettingsHeader>{displayContent.headerDetails}</SettingsHeader>
                <SettingsForm isReviewTemplateTakeover />
              </StyledSettingsWrapper>
              {(templateHasHandlebars || !!dataFeedCount) && (
                <StyledSettingsWrapper
                  className={
                    submitData?.data?.submitTemplate?.message === MISSING_USER_DATA
                      ? 'hasError'
                      : ''
                  }
                >
                  <SettingsHeader>Sample Data</SettingsHeader>
                  <Disclaimer>
                    Load a sample user for your dynamic content to help Meta review and approve your
                    template.
                  </Disclaimer>
                  <DataPane
                    onRefreshPreview={onRefreshPreview}
                    showNullForDataFeedEmptyState
                    showUserData={templateHasHandlebars}
                  />
                </StyledSettingsWrapper>
              )}
            </div>
            <Preview />
          </BodyContent>
        </BodyWrapper>
      }
      Footer={
        <FooterWrapper>
          <SecondaryButton onClick={onClose}>
            <ArrowBack /> Keep editing
          </SecondaryButton>
          <PrimaryButton
            isLoading={submitData?.loading}
            onClick={() => {
              submitTemp();
            }}
          >
            Submit for approval
          </PrimaryButton>
        </FooterWrapper>
      }
    />
  );
};
